/**
 * @file    partitionValidation.ts
 * @scope   SHARED
 * @summary Partition-level validation + display helpers: field-count labels, data-source validity,
 *          and the top-level `isEnterpriseDataInvalid` guard used by the Save flow.
 * @consumes DataSource/EnterpriseGlobal/FieldsManager models, CustomConfig
 * @produces `getDataSourceFieldCount`, `isDataSourceItemInvalid`, `isEnterpriseDataInvalid`, etc.
 * @sideEffects None — pure.
 * @notes The Save guard short-circuits here for Enterprise widgets before the legacy rule engine runs.
 */
import { PartitionModel, DataSourceItem } from 'models/Enterprise/DataSourceModel';
import { EnterpriseGlobalModel } from 'models/Enterprise/EnterpriseGlobalModel';
import { SelectedUserFields } from 'models/Enterprise/FieldsManagerModel';
import { CustomConfig } from 'models/WidgetModel';

/** Default label displayed when a partition has no associated fields. */
export const DATA_SOURCE_NO_FIELDS_LABEL = 'No fields';

/**
 * Returns a human-readable label describing how many fields are configured
 * for a given partition (e.g. "2 fields", "1 field", or "No fields").
 * @param selectedValue - The partition to look up
 * @param selectedEnterpriseFields - All configured field groups keyed by partition
 * @returns A display string with the field count
 */
export const getDataSourceFieldCount = (
  selectedValue: PartitionModel | null,
  selectedEnterpriseFields: SelectedUserFields[]
): string => {
  if (!selectedValue) return DATA_SOURCE_NO_FIELDS_LABEL;

  if (selectedEnterpriseFields && selectedEnterpriseFields.length > 0) {
    const entry = selectedEnterpriseFields.find(
      (fields: any) => fields.partitionGuid === selectedValue.value
    );

    if (entry) {
      const count = entry.fields?.length || 0;
      if (count === 0) return DATA_SOURCE_NO_FIELDS_LABEL;
      if (count === 1) return '1 field';
      return `${count} fields`;
    }
  }

  return DATA_SOURCE_NO_FIELDS_LABEL;
};

/**
 * Checks whether a partition has zero configured fields.
 * @param selectedValue - The partition to check
 * @param selectedEnterpriseFields - All configured field groups keyed by partition
 * @returns true if the partition has no fields
 */
export const dataSourceItemHasNoFields = (
  selectedValue: PartitionModel | null,
  selectedEnterpriseFields: SelectedUserFields[]
): boolean =>
  getDataSourceFieldCount(selectedValue, selectedEnterpriseFields) === DATA_SOURCE_NO_FIELDS_LABEL;

/**
 * Determines whether a data source item should be shown as invalid.
 * An item is invalid when it has been touched by the user and has no fields.
 * @param source - The data source item to validate
 * @param touchedItemIds - Set of item ids the user has interacted with
 * @param selectedEnterpriseFields - All configured field groups keyed by partition
 * @returns true if the item is touched and has no fields
 */
export const isDataSourceItemInvalid = (
  source: DataSourceItem,
  touchedItemIds: Set<string>,
  selectedEnterpriseFields: SelectedUserFields[]
): boolean =>
  touchedItemIds.has(source.id) &&
  dataSourceItemHasNoFields(source.selectedValue ?? null, selectedEnterpriseFields);

/**
 * Validates enterprise configuration and checks if it should disable the widget
 * @param enterpriseOptions - The enterprise options containing selected partitions and fields
 * @param customWidgetConfig - The custom widget configuration
 * @returns true if the widget should be disabled, false otherwise
 */
export const isEnterpriseDataInvalid = (
  enterpriseOptions: EnterpriseGlobalModel,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _customWidgetConfig: CustomConfig | undefined
): boolean => {
  // Check if no partitions are selected
  const selectedPartitions = enterpriseOptions.selectedValues.selectedPartitions;
  if (!selectedPartitions || selectedPartitions.length === 0) {
    return true;
  }

  // Check if any selected partition has no fields associated with it
  const selectedEnterpriseFields = enterpriseOptions.selectedValues.selectedEnterpriseFields;
  const hasPartitionWithoutFields = selectedPartitions.some((partition) => {
    const fieldsForPartition = selectedEnterpriseFields.find(
      (field: any) => field.partitionGuid === partition.value
    );
    return (
      !fieldsForPartition || !fieldsForPartition.fields || fieldsForPartition.fields.length === 0
    );
  });

  return hasPartitionWithoutFields;
};
